/**
 * Sorts rows in the browser for the small lists that page client-side. An
 * accessor per column returns a string, number, Date or nothing; strings
 * compare by letter ignoring case and accents (digits in order, so "Item 2" precedes "Item 10"), and a row with no value goes
 * last whichever way the column runs, so "ascending" never buries the data
 * under blanks.
 */

export type SortValue = string | number | Date | null | undefined;
export type SortOrder = 'asc' | 'desc';

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

function isBlank(value: SortValue): value is null | undefined {
  return value === null || value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value));
}

function compareValues(a: NonNullable<SortValue>, b: NonNullable<SortValue>): number {
  if (typeof a === 'string' && typeof b === 'string') return collator.compare(a, b);
  const left = a instanceof Date ? a.getTime() : a;
  const right = b instanceof Date ? b.getTime() : b;
  return left < right ? -1 : left > right ? 1 : 0;
}

export function sortRows<T>(
  rows: readonly T[],
  key: string,
  order: SortOrder,
  accessors: Record<string, (row: T) => SortValue>,
): T[] {
  const read = accessors[key];
  if (!read) return [...rows];
  const sign = order === 'asc' ? 1 : -1;
  // Array.prototype.sort is stable, so equal rows keep the order they came in.
  return rows
    .map((row) => ({ row, value: read(row) }))
    .sort((x, y) => {
      if (isBlank(x.value)) return isBlank(y.value) ? 0 : 1;
      if (isBlank(y.value)) return -1;
      return sign * compareValues(x.value, y.value);
    })
    .map(({ row }) => row);
}
